import { useOrganizationList } from "@clerk/nextjs";
import { ChevronLeft } from "lucide-react";

import { clearStoredFormData, markOnboardingSessionActive } from "@/components/hooks/useOnboardingNavigation";
import LoadingButton from "@/components/LoadingButton";
import { StepHeader } from "@/components/onboarding/StepHeader";
import { Button } from "@/components/ui/button";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { api, handleApiRequest } from "@/lib/utils";
import { IndustrySelector } from "@/pages/select-industry";

import type { CompleteOnboardingFormData } from "@/lib/zod-schemas/onboarding";
import type { OnboardingStepProps } from "@/types/onboarding";

export function CompanyDetailsBasicsStep({
  form,
  onNext,
  onBack,
  canGoBack,
}: OnboardingStepProps<CompleteOnboardingFormData>) {
  const { setActive } = useOrganizationList();

  const handleContinue = async (data: CompleteOnboardingFormData) => {
    const isValid = await form.trigger(["companyName", "industry", "websiteUrl"]);
    if (isValid) {
      await handleApiRequest({
        makeRequest: async () => {
          const response = await api.post("/api/onboarding", data);
          return response.data;
        },
        successCallback: async (response: { data: { organizationId: string; websiteId: string } }) => {
          form.setValue("reboltOrganizationId", response.data.organizationId);
          form.setValue("reboltWebsiteId", response.data.websiteId);

          markOnboardingSessionActive();

          if (setActive) {
            await setActive({ organization: response.data.organizationId });
            clearStoredFormData();
          }

          onNext();
        },
      });
    }
  };

  return (
    <div className="flex flex-col justify-start lg:justify-center h-full px-5 lg:px-40 py-6 lg:py-0 w-full">
      {canGoBack && (
        <Button
          type="button"
          variant="rebolt-outline"
          size="sm"
          onClick={onBack}
          className="tracking-tight w-fit lg:mb-12 mb-4 gap-2 p-2.5 lg:px-6 lg:py-2"
          iconPosition="start"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden lg:inline-block">Back</span>
        </Button>
      )}
      <StepHeader title="Verify your details" subtitle="Confirm your business name and industry you serve." />

      <div className="mt-6 lg:mt-12 w-full flex flex-col lg:gap-9 gap-6">
        <FormField
          control={form.control}
          name="companyName"
          render={({ field }) => (
            <FormItem className="flex flex-col mb-0">
              <FormLabel className="text-ash-700 font-semibold lg:text-base text-sm">Business Name</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="text"
                  placeholder="Company Name"
                  className="text-sm mb-0 h-fit px-4 py-3 rounded-xl border-ash-300 bg-white"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-col gap-2">
          <IndustrySelector
            defaultSelected={form.formState?.defaultValues?.industry}
            labelClassName="text-ash-700 font-semibold lg:text-base text-sm mb-2"
            inputClassName="text-sm mb-0 h-fit px-4 py-3 rounded-xl border-ash-300 bg-white focus:ring-1 focus:ring-ash-500 border-1 focus:border-ash-300"
            onSelect={(industry) => {
              form.setValue("industry", industry, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
            }}
            showIcon={false}
            placeholder="Industry..."
            headingText="Industry"
          />
          {form.formState?.errors?.industry && (
            <FormMessage>{form.formState.errors.industry.message?.toString()}</FormMessage>
          )}
        </div>

        <FormField
          control={form.control}
          name="websiteUrl"
          render={({ field }) => (
            <FormItem className="flex flex-col mb-0">
              <FormLabel className="text-ash-700 font-semibold lg:text-base text-sm">
                Website <span className="text-ash-500 text-xs ml-0.5">Optional</span>
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="text"
                  placeholder="www.example.com"
                  className="text-sm mb-0 h-fit px-4 py-3 rounded-xl border-ash-300 bg-white"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="flex gap-4 lg:mt-12 mt-auto">
        <LoadingButton
          type="button"
          variant="rebolt-secondary"
          size="lg"
          className="justify-center lg:max-w-fit tracking-tight lg:py-3 py-2"
          onClick={async () => {
            await handleContinue(form.getValues());
          }}
        >
          Continue
        </LoadingButton>
      </div>
    </div>
  );
}
